/**
 * Funeral escorts: requests from the funeral homes' Jotform (or entered by hand), staffed by a supervisor,
 * and shown on everyone's Home.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, PeoplePicker, when } from './common.tsx';

const STATUS: Record<string, string> = { new: 'Needs deputies', scheduled: 'Scheduled', done: 'Done', declined: 'Declined', canceled: 'Canceled' };
const TONE: Record<string, 'warn' | 'ok' | 'neutral' | 'bad' | 'info'> = { new: 'warn', scheduled: 'info', done: 'ok', declined: 'neutral', canceled: 'neutral' };
const toLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
const whenText = (e: any) => (e.service_at ? when(e.service_at, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : e.service_text ? `${e.service_text} (check)` : 'No date');

export function EscortsPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState('open');
  const r = useLoad(() => api.get<any>(`/api/escorts?status=${status}`), [status]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Agency"
        title="Funeral escorts"
        sub="Requests from the funeral homes’ form. Everyone sees what’s coming on their Home; a supervisor puts deputies on each one."
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => navigate(at('/escorts/new'))}>
              Enter a request
            </button>
          )
        }
      />
      <div className="seg-ctl" role="tablist" aria-label="Which escorts">
        {(
          [
            ['open', 'Coming up'],
            ['past', 'Past'],
            ['all', 'All'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
            {l}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.escorts.length ? (
        <EmptyState icon="calendar" title={status === 'open' ? 'No escorts coming up' : 'None'} />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>For</th>
                <th>Funeral home</th>
                <th>Service → cemetery</th>
                <th>Deputies</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.escorts.map((e: any) => (
                <tr key={e.id} className="clickable" onClick={() => navigate(at(`/escorts/${e.id}`))}>
                  <td>
                    <strong>{whenText(e)}</strong>
                    <div className="small muted mono">{e.number}</div>
                  </td>
                  <td>{e.deceased ?? '—'}</td>
                  <td>{e.funeral_home ?? '—'}</td>
                  <td>{[e.service_place, e.cemetery].filter(Boolean).join(' → ') || '—'}</td>
                  <td>{e.deputy_list.map((p: any) => p.name).join(', ') || '—'}</td>
                  <td>
                    <Badge tone={TONE[e.status]} dot>
                      {STATUS[e.status]}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.data?.canManage && <JotformSetup />}
    </div>
  );
}

function JotformSetup() {
  const r = useLoad(() => api.get<any>('/api/escorts/settings'));
  const [path, setPath] = useState<string | null>(null);
  const a = useAction();
  if (!r.data) return null;
  const j = r.data.jotform;
  return (
    <Card title="Connect the funeral escort Jotform">
      <div className="stack sm">
        <p className="small">
          In Jotform, open the escort request form → <strong>Settings → Integrations → WebHooks</strong>, paste the link below and save. Every request a funeral home sends then shows up here and on everyone’s Home. Fields are matched by their names (deceased, funeral home, date, time, church, cemetery, contact…); anything else is kept under “More from the form”.
        </p>
        <p className="small muted">
          {j.configured ? `Link made ${when(j.createdAt)}.` : 'No link yet.'} {j.lastReceivedAt ? `Last request from Jotform ${when(j.lastReceivedAt)}.` : j.configured ? 'Nothing received yet.' : ''}
        </p>
        {path && (
          <Notice tone="ok">
            <div className="small">Copy this now (it isn’t shown again):</div>
            <code className="esc-link">{location.origin + path}</code>
          </Notice>
        )}
        <ErrorBox error={a.error} />
        <div>
          <button
            className="btn"
            disabled={a.busy}
            onClick={() => (!j.configured || window.confirm('Make a new link? The old one stops working, so update Jotform with the new one.')) && a.run(async () => (setPath((await api.post<{ path: string }>('/api/escorts/settings/link')).path), r.reload()))}
          >
            {j.configured ? 'Make a new link' : 'Make the Jotform link'}
          </button>
        </div>
      </div>
    </Card>
  );
}

const FIELDS: [string, string, string?][] = [
  ['deceased', 'Deceased'],
  ['funeralHome', 'Funeral home'],
  ['servicePlace', 'Service (church / chapel)'],
  ['cemetery', 'Cemetery'],
  ['route', 'Route'],
  ['vehicles', 'Vehicles in the procession'],
  ['contactName', 'Contact'],
  ['contactPhone', 'Contact phone'],
];

export function NewEscort() {
  const { navigate } = useRouter();
  const [f, setF] = useState<Record<string, string>>({ serviceAt: '' });
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="Funeral escorts" title="Enter an escort request" sub="For a request that came by phone or email." />
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ id: string }>('/api/escorts', { ...f, serviceAt: f.serviceAt ? new Date(f.serviceAt).toISOString() : undefined });
            navigate(at(`/escorts/${r.id}`), true);
          });
        }}
      >
        <Card title="The escort">
          <div className="grid-3">
            <label>
              Date and time
              <input type="datetime-local" value={f.serviceAt} onChange={(e) => setF({ ...f, serviceAt: e.target.value })} required />
            </label>
            {FIELDS.map(([k, l]) => (
              <label key={k}>
                {l}
                <input value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              </label>
            ))}
          </div>
          <label>
            Notes
            <textarea rows={3} value={f.details ?? ''} onChange={(e) => setF({ ...f, details: e.target.value })} />
          </label>
        </Card>
        <ErrorBox error={a.error} />
        <div>
          <button className="btn primary" disabled={a.busy}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export function EscortDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/escorts/${id}`), [id]);
  const a = useAction();
  const [deps, setDeps] = useState<string[] | null>(null);
  const [at_, setAt] = useState<string | null>(null);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { escort: e, events, canManage, people, mine } = r.data;
  const picked = deps ?? (e.deputies as string[]);
  const timeVal = at_ ?? toLocal(e.service_at);
  const save = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/escorts/${id}`, body), setDeps(null), setAt(null), r.reload()));
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Funeral escorts"
        title={`${whenText(e)}${e.deceased ? ` · ${e.deceased}` : ''}`}
        sub={`${e.number} · ${e.funeral_home ?? 'funeral home not given'} · received ${when(e.received_at)}`}
        actions={
          <Badge tone={TONE[e.status]} dot>
            {STATUS[e.status]}
          </Badge>
        }
      />
      {mine && e.status === 'scheduled' && <Notice tone="info">You’re on this escort.</Notice>}
      {!e.service_at && <Notice tone="warn">The form’s date couldn’t be read{e.service_text ? ` (“${e.service_text}”)` : ''}. Set the date and time below.</Notice>}
      <ErrorBox error={a.error} />
      <div className="grid-2-cards">
        <Card title="The funeral">
          <dl className="kv">
            <dt>Deceased</dt>
            <dd>{e.deceased ?? '—'}</dd>
            <dt>Funeral home</dt>
            <dd>{e.funeral_home ?? '—'}</dd>
            <dt>Service</dt>
            <dd>{e.service_place ?? '—'}</dd>
            <dt>Cemetery</dt>
            <dd>{e.cemetery ?? '—'}</dd>
            <dt>Route</dt>
            <dd>{e.route ?? '—'}</dd>
            <dt>Vehicles</dt>
            <dd>{e.vehicles ?? '—'}</dd>
            <dt>Contact</dt>
            <dd>{[e.contact_name, e.contact_phone, e.contact_email].filter(Boolean).join(' · ') || '—'}</dd>
          </dl>
          {e.details && (
            <>
              <div className="small strong" style={{ marginTop: 8 }}>
                More from the form
              </div>
              <p className="small pre">{e.details}</p>
            </>
          )}
        </Card>
        <Card title="Deputies">
          {canManage ? (
            <div className="stack sm">
              <label>
                Date and time
                <input type="datetime-local" value={timeVal} onChange={(ev) => setAt(ev.target.value)} />
              </label>
              <PeoplePicker people={people.map((p: any) => ({ id: p.id, name: p.name, title: p.title, call_number: p.call_number }))} value={picked} onChange={setDeps} label="Deputies on this escort" />
              <div className="row gap-sm wrap">
                <button className="btn primary" disabled={a.busy} onClick={() => save({ deputies: picked, serviceAt: timeVal ? new Date(timeVal).toISOString() : null, status: picked.length && e.status === 'new' ? 'scheduled' : undefined })}>
                  Save
                </button>
                {e.status !== 'done' && e.status !== 'new' && (
                  <button className="btn" disabled={a.busy} onClick={() => save({ status: 'done' })}>
                    Mark done
                  </button>
                )}
                {['new', 'scheduled'].includes(e.status) && (
                  <>
                    <button
                      className="btn ghost"
                      disabled={a.busy}
                      onClick={() => {
                        const note = window.prompt('Why can’t we do it? (No one available, outside the county…)');
                        if (note) save({ status: 'declined', note });
                      }}
                    >
                      Decline
                    </button>
                    <button
                      className="btn ghost"
                      disabled={a.busy}
                      onClick={() => {
                        const note = window.prompt('Canceled by whom / why?');
                        if (note) save({ status: 'canceled', note });
                      }}
                    >
                      Canceled
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <p>{e.deputy_list.map((p: any) => p.name).join(', ') || 'No one yet.'}</p>
          )}
          {e.note && <p className="small muted">Note: {e.note}</p>}
        </Card>
      </div>
      <Card title="History">
        <ul className="timeline">
          {events.map((v: any, i: number) => (
            <li key={i}>
              <strong>{v.action}</strong> <span className="muted small">{when(v.at)} · {v.by_name ?? 'Jotform'}</span>
              {v.note && <div className="small">{v.note}</div>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
